import Loader from './Loader';
import Notifications from '../utils/Notifications';

/**
 * @typedef {import('../types/Sequences').Sequence} Sequence
 */

/**
 * Utility class for loading sequences and their associated assets.
 */
export default class SequenceLoader {
  /**
   * Loads a sequence by ID.
   * @param {number|null} id - The ID of the sequence to load.
   * @returns {Promise<Sequence|null>} The loaded sequence data, or null if no ID provided.
   */
  static async loadSequence(id) {
    if (!id) {
      return null;
    }

    return Loader.loadSequence(id)
      .then(async sequence => {
        return SequenceLoader.#reverseMap(sequence);
      })
      .catch(error => {
        Notifications.error('Network error while loading Sequence', error, { id });
      });
  }

  /**
   * Reverse maps a sequence to include its assets.
   * @param {Sequence} sequence - The sequence data from the API.
   * @returns {Promise<Sequence>} The mapped sequence data.
   */
  static async #reverseMap(sequence) {
    sequence.sequenceEntries = sequence.sequenceEntries || [];

    const assets = await Promise.all(
      sequence.sequenceEntries.map(async entry => {
        return Loader.loadSign(entry.sign_id);
      }),
    );

    sequence.sequenceEntries.forEach((entry, index) => {
      entry.asset = assets[index];

      if (!entry.asset) {
        throw new Error(`Failed to load asset for entry: ${JSON.stringify(entry)}`);
      }

      entry.asset.playlist_id = entry.playlist_id;
      entry.asset.repeat = entry.repeat;
    });

    sequence.targetResolution = {
      x: sequence.sequenceEntries[0]?.asset?.width,
      y: sequence.sequenceEntries[0]?.asset?.height,
    };

    sequence.assets = assets;

    return sequence;
  }
}
